/* Source drop zone — shown by single-source actions until a source image is
   picked (via drawer drag here, or the ✨ button on a card). */
import { PhotoIcon } from "@heroicons/react/24/outline"
import { useDroppable, useDndMonitor, type DragEndEvent } from "@dnd-kit/core"
import { toast } from "react-toastify"
import { DRAWER_ITEM_PREFIX } from "../SelectedAssetsDrawer"
import type { ScrapedImage } from "../../types"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import { SOURCE_DROP_ID } from "./constants"

interface SourceDropZoneProps {
  actionLabel: string
  /** True when a source IS set but it's a video — this action needs an
   *  image, so the copy explains the mismatch instead of just "drop here". */
  isVideoSource: boolean
  /** Accept dropped videos too (brand overlay draws on either). */
  allowVideo?: boolean
  /** Usable source images (staged + pool), shown as click-to-pick thumbs —
   *  the ✨ card grid is hidden while a flow is open, so clicking here is
   *  the primary way to choose a product. */
  candidates?: ScrapedImage[]
  onSelectAsset?: (asset: ScrapedImage) => void
}

export default function SourceDropZone({
  actionLabel,
  isVideoSource,
  allowVideo = false,
  candidates = [],
  onSelectAsset,
}: SourceDropZoneProps) {
  const droppable = useDroppable({
    id: SOURCE_DROP_ID,
    data: { kind: "ai-source" },
  })

  useDndMonitor({
    onDragEnd(event: DragEndEvent) {
      const { active, over } = event
      if (!over || String(over.id) !== SOURCE_DROP_ID) return
      const id = String(active.id)
      if (!id.startsWith(DRAWER_ITEM_PREFIX)) return
      const data = active.data.current as
        | { kind: "pool"; asset: ScrapedImage }
        | undefined
      const dropped = data?.asset
      if (!dropped) return
      if (dropped.type === "video" && !allowVideo) {
        toast.warning(
          `${actionLabel} needs an image source — try Slideshow for videos`,
        )
        return
      }
      onSelectAsset?.(dropped)
    },
  })

  return (
    <div
      ref={droppable.setNodeRef}
      className={cn(
        "min-h-[10rem] rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-2 text-center px-6 py-8 transition",
        droppable.isOver
          ? "border-brand bg-brand/10 text-brand"
          : "border-system-gray-700/50 text-system-gray-500",
      )}
    >
      <PhotoIcon className="w-8 h-8 opacity-50" />
      <span className="text-xs font-semibold">
        {droppable.isOver
          ? "Drop to use as source"
          : isVideoSource
            ? `${actionLabel} needs an image source`
            : `Pick a source image for ${actionLabel}`}
      </span>
      <span className="text-[11px]">
        {candidates.length > 0
          ? "Click an image below, or drag one from the Selected Assets drawer here."
          : "Drag an image from the Selected Assets drawer here."}
      </span>
      {candidates.length > 0 && (
        <div className="flex gap-2 flex-wrap justify-center mt-1">
          {candidates.map(a => (
            <button
              key={a.id}
              onClick={() => onSelectAsset?.(a)}
              className="w-16 h-16 rounded-lg overflow-hidden ring-1 ring-system-gray-700/50 hover:ring-brand transition"
              title="Use as the product source"
            >
              <img
                src={a.thumbnailUrl || a.url}
                alt=""
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
